import { useRef, useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/da227u3k8r5te5mqse80";

const contactInfo = [
  {
    icon: "ri-mail-line",
    label: "投稿与反馈邮箱",
    value: "feedback@kaixinmanhua.example",
  },
  {
    icon: "ri-time-line",
    label: "反馈处理时效",
    value: "工作日 24 小时内回复",
  },
  {
    icon: "ri-community-line",
    label: "官方读者群",
    value: "加入社群一起追更吐槽",
  },
];

type FormStatus = "idle" | "submitting" | "success" | "error";

export default function ContactSection() {
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<FormStatus>("idle");
  const [formError, setFormError] = useState("");

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = ((fd.get("website_alt") as string) || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }
    fd.delete("website_alt");

    const params = new URLSearchParams();
    fd.forEach((value, key) => {
      params.append(key, String(value));
    });

    setStatus("submitting");
    setFormError("");
    try {
      const res = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta as Record<string, unknown>) || {};
      const serverMsg =
        (meta?.message as string) ||
        (parsed?.message as string) ||
        (meta?.detail as string) ||
        responseText;
      if (res.ok && parsed?.code === "OK") {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  }

  return (
    <section id="contact" className="relative w-full bg-background-50 py-12 md:py-16">
      <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-6 md:mb-10">
          <span className="inline-flex items-center gap-1.5 text-accent-600 text-sm font-bold mb-1">
            <i className="ri-chat-3-fill" aria-hidden="true"></i>
            联系我们
          </span>
          <h2 className="font-heading text-foreground-950 text-2xl md:text-4xl">投稿 &amp; 意见反馈</h2>
          <p className="text-foreground-600 text-sm mt-2 max-w-xl mx-auto">
            有好看的沙雕漫画想推荐，或有任何建议，都欢迎告诉我们。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Contact info */}
          <aside className="lg:col-span-2 flex flex-col gap-4">
            {contactInfo.map((item) => (
              <div
                key={item.label}
                className="flex items-start gap-3 p-4 md:p-5 rounded-2xl bg-background-100 border-2 border-background-200"
              >
                <span className="w-10 h-10 shrink-0 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-lg shadow-comic-sm">
                  <i className={item.icon} aria-hidden="true"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">{item.label}</p>
                  <p className="text-sm font-bold text-foreground-900 mt-0.5">{item.value}</p>
                </div>
              </div>
            ))}
            <div className="rounded-2xl bg-accent-100 border-2 border-accent-200 p-4 md:p-5">
              <p className="text-sm font-bold text-accent-900 flex items-center gap-2">
                <i className="ri-lightbulb-flash-fill" aria-hidden="true"></i>
                找一本漫画找不到？
              </p>
              <p className="text-xs text-accent-800/90 mt-1.5 leading-relaxed">
                记住大致剧情或主角特征，把线索写进表单，我们会帮你一起找出来。
              </p>
            </div>
          </aside>

          {/* Form */}
          <div className="lg:col-span-3">
            <form
              ref={formRef}
              id="feedbackForm"
              onSubmit={onSubmit}
              data-readdy-form
              className="rounded-3xl bg-background-100 border-2 border-background-200 p-5 md:p-7"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="fb-name" className="block text-sm font-bold text-foreground-900 mb-1.5">
                    称呼
                  </label>
                  <input
                    id="fb-name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼你"
                    className="w-full px-3.5 py-2.5 rounded-lg bg-background-50 border-2 border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-400"
                  />
                </div>
                <div>
                  <label htmlFor="fb-email" className="block text-sm font-bold text-foreground-900 mb-1.5">
                    邮箱
                  </label>
                  <input
                    id="fb-email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    className="w-full px-3.5 py-2.5 rounded-lg bg-background-50 border-2 border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-400"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="fb-type" className="block text-sm font-bold text-foreground-900 mb-1.5">
                    反馈类型
                  </label>
                  <select
                    id="fb-type"
                    name="feedbackType"
                    className="w-full px-3.5 py-2.5 rounded-lg bg-background-50 border-2 border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400"
                  >
                    <option>推荐好看的沙雕漫画</option>
                    <option>报错 / 内容问题</option>
                    <option>功能建议</option>
                    <option>找一本漫画</option>
                    <option>其他</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="fb-msg" className="block text-sm font-bold text-foreground-900 mb-1.5">
                    留言内容
                  </label>
                  <textarea
                    id="fb-msg"
                    name="message"
                    required
                    maxLength={500}
                    rows={5}
                    placeholder="写下你想说的，包括作品名称、大致剧情线索或你的建议…"
                    className="w-full px-3.5 py-2.5 rounded-lg bg-background-50 border-2 border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 placeholder:text-foreground-400 resize-none"
                  ></textarea>
                  <p className="text-[11px] text-foreground-500 mt-1 text-right">最多 500 字</p>
                </div>
              </div>

              {/* Honeypot */}
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="comic-honeypot"
              />

              <div className="mt-5 flex flex-col sm:flex-row sm:items-center gap-3">
                <button
                  type="submit"
                  disabled={status === "submitting"}
                  className="px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-bold hover:bg-primary-600 transition whitespace-nowrap disabled:opacity-60 cursor-pointer"
                >
                  {status === "submitting" ? "提交中…" : "提交反馈"}
                </button>
                {status === "success" && (
                  <p className="text-sm text-primary-700 flex items-center gap-1.5">
                    <i className="ri-checkbox-circle-fill" aria-hidden="true"></i>
                    已收到你的反馈，感谢支持！
                  </p>
                )}
                {status === "error" && formError && (
                  <p className="text-sm text-accent-700 flex items-center gap-1.5">
                    <i className="ri-error-warning-fill" aria-hidden="true"></i>
                    {formError}
                  </p>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}